import { useTranslation } from 'react-i18next'
import { Tag, Sparkles, Car, Bike, ArrowRight } from 'lucide-react'
import { useBooking } from '../../store/bookingStore'
import { formatCents } from '../../lib/money'
import { hhmm, prettyDayWeekday, nightsBetween } from '../../lib/format'
import type { Locale } from '../../lib/api/types'
import { cn } from '../../lib/cn'

function Row({ label, value, muted, accent }: { label: string; value: string; muted?: boolean; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className={cn(muted && 'text-ink-400', accent && 'text-brand-600')}>{label}</span>
      <span className={cn('font-medium tabular-nums', accent ? 'text-brand-600' : 'text-ink-800')}>{value}</span>
    </div>
  )
}

export function PriceSummary({ className }: { className?: string }) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as Locale
  const calc = useBooking((s) => s.calculatedInfo)
  const cart = useBooking((s) => s.cart)

  if (!calc) {
    return (
      <div className={cn('gsm-card p-5', className)}>
        <h3 className="font-display text-sm font-bold text-ink-900">{t('price.summary')}</h3>
        <p className="mt-2 text-sm text-ink-400">{t('price.emptyHint')}</p>
      </div>
    )
  }

  const services = calc.serviceInfoList ?? []
  const discount =
    (calc.conventionInfo?.subtractionValue ?? 0) +
    (calc.couponInfo?.subtractionValue ?? 0) +
    (calc.pCardInfo?.subtractionValue ?? 0)

  return (
    <div className={cn('gsm-card overflow-hidden', className)}>
      <div className="border-b border-ink-100 bg-ink-50/60 px-5 py-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-sm font-bold text-ink-900">{t('price.summary')}</h3>
          {calc.transport?.label && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ink-100 px-2.5 py-1 text-xs font-semibold text-ink-800">
              {/^mot/i.test(calc.transport.label) ? (
                <Bike className="h-3.5 w-3.5 text-brand-600" />
              ) : (
                <Car className="h-3.5 w-3.5 text-brand-600" />
              )}
              <span className="truncate">{calc.transport.label}</span>
            </span>
          )}
        </div>

        {calc.fromDay && (
          <div className="mt-3.5 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div className="min-w-0">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-500">
                {t('price.entry')}
              </p>
              <p className="mt-0.5 truncate font-display text-sm font-semibold text-ink-900">
                {prettyDayWeekday(calc.fromDay, locale)}
              </p>
              <p className="font-display text-2xl font-bold leading-tight tabular-nums text-brand-600">
                {hhmm(calc.fromHour)}
              </p>
            </div>

            <div className="flex flex-col items-center gap-1.5 px-1">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-ink-400 ring-1 ring-ink-100">
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="whitespace-nowrap rounded-full bg-ink-100 px-2 py-0.5 text-[0.65rem] font-semibold text-ink-800">
                {nightsBetween(calc.fromDay, calc.toDay) > 0
                  ? t('price.nights', { count: nightsBetween(calc.fromDay, calc.toDay) })
                  : t('price.sameDay')}
              </span>
            </div>

            <div className="min-w-0 text-right">
              <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-ink-500">
                {t('price.exit')}
              </p>
              <p className="mt-0.5 truncate font-display text-sm font-semibold text-ink-900">
                {prettyDayWeekday(calc.toDay, locale)}
              </p>
              <p className="font-display text-2xl font-bold leading-tight tabular-nums text-brand-600">
                {hhmm(calc.toHour)}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="px-5 py-3">
        <Row label={t('price.rate')} value={formatCents(calc.rateAmount, locale)} />
        {services.map((s, i) => (
          <Row key={i} label={s.label} value={formatCents(s.addictionalValue, locale)} muted />
        ))}
        {calc.conventionInfo && (
          <Row label={`${t('price.convention')} · ${calc.conventionInfo.label}`} value={`− ${formatCents(calc.conventionInfo.subtractionValue, locale)}`} accent />
        )}
        {calc.couponInfo && (
          <Row label={`${t('price.coupon')} · ${calc.couponInfo.label}`} value={`− ${formatCents(calc.couponInfo.subtractionValue, locale)}`} accent />
        )}
        {calc.specialOffer && (
          <div className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
            <Sparkles className="h-3.5 w-3.5" /> {t('price.specialOffer')}
          </div>
        )}
      </div>
      <div className="border-t border-ink-100 px-5 py-4">
        <div className="flex items-end justify-between">
          <span className="font-display text-sm font-bold text-ink-900">{t('price.total')}</span>
          <div className="text-right">
            {discount > 0 && calc.fullOriginalPrice > calc.discountedAmount && (
              <span className="mr-2 text-sm text-ink-300 line-through">
                {formatCents(calc.fullOriginalPrice, locale)}
              </span>
            )}
            <span className="font-display text-2xl font-extrabold tracking-tight text-ink-900">
              {formatCents(calc.discountedAmount, locale)}
            </span>
          </div>
        </div>
        {cart.couponCode && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-400">
            <Tag className="h-3 w-3" /> {cart.couponCode}
          </div>
        )}
      </div>
    </div>
  )
}
